import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Calculator, ChevronRight, ChevronDown, RotateCcw, Heart, Activity, CircleDot, FileSpreadsheet, TrendingUp, Save } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { PageLayout, PageHeader, PageCard, pageStyles } from '../PageLayout';

import provisionRulesData from '../../data/provisionRulesData.json';

type ProvisionRule = {
  sparte: string;
  bemessungsgrundlage: string;
  neuVerl: string;
  provisionssatz: number;
  einheit: string;
  faktor: number;
};

type ProvisionRulesByStufe = Record<string, ProvisionRule[]>;

const STUFE_LABELS: Record<string, string> = {
  AGT: 'Agentur',
  HA: 'Hauptagentur',
  GA1: 'Generalagentur 1',
  GA2: 'Generalagentur 2',
  BD: 'Bezirksdirektion',
};

/** Vermittler-Stufe aus Mitarbeiterdaten (z. B. "Hauptagent") auf Provisions-Key (z. B. HA) mappen. */
function userLevelToStufeKey(level: string | undefined): string {
  if (!level || typeof level !== 'string') return 'AGT';
  const t = level.trim();
  if (t === 'Bezirksdirektor') return 'BD';
  if (t === 'Generalagent II') return 'GA2';
  if (t === 'Generalagent') return 'GA1';
  if (t === 'Hauptagent') return 'HA';
  if (t === 'Agent') return 'AGT';
  if (t === 'Innendienst' || t === 'none' || t === '') return 'AGT';
  return 'AGT';
}

const SPARTE_ICONS: Record<string, React.ReactNode> = {
  Kranken: <Heart size={18} />,
  Leben: <Activity size={18} />,
  Risikounfall: <CircleDot size={18} />,
  'Vita Life': <Activity size={18} />,
  UBR: <CircleDot size={18} />,
  STHp: <FileSpreadsheet size={18} />,
  STHg: <FileSpreadsheet size={18} />,
  KFZ: <CircleDot size={18} />,
  Rechtschutz: <FileSpreadsheet size={18} />,
  Bausparen: <FileSpreadsheet size={18} />,
  Bank: <FileSpreadsheet size={18} />,
  FI: <FileSpreadsheet size={18} />,
  Invest: <TrendingUp size={18} />,
};

function getSparteIcon(sparte: string): React.ReactNode {
  return SPARTE_ICONS[sparte] ?? <CircleDot size={18} />;
}

// Regeln nach Kategorie gruppieren (Sparte; leere Sparte = gleiche Kategorie wie vorher)
function groupRulesBySparte(rules: ProvisionRule[]): { name: string; rules: ProvisionRule[] }[] {
  const groups: { name: string; rules: ProvisionRule[] }[] = [];
  let currentName = '';
  for (const r of rules) {
    if (r.bemessungsgrundlage === 'Summe' || (r.sparte === '' && r.neuVerl === '' && r.provisionssatz === 0)) continue;
    if (r.sparte && r.sparte.trim() !== '') {
      currentName = r.sparte.trim();
      groups.push({ name: currentName, rules: [r] });
    } else if (groups.length > 0) {
      groups[groups.length - 1].rules.push(r);
    }
  }
  return groups;
}

/**
 * Provision nach Excel-Formeln aus prov.xls:
 * - MB (Monatsbeitrag): Betrag * Provisionssatz / 100
 * - %o (Promille): Betrag * Provisionssatz / 1000
 * - % + Monatsbeitrag: Betrag * 12 * (100/119) * Provisionssatz/100  (Netto-Umrechnung)
 * - % + Jahresbeitrag: Betrag * (100/119) * Provisionssatz/100
 * - UBR Monatsbeitrag (ohne 119): Betrag * 12 * Provisionssatz/100
 */
function berechneProvision(rule: ProvisionRule, betrag: number): number {
  if (!betrag || isNaN(betrag)) return 0;
  const satz = rule.provisionssatz;
  const basis = rule.bemessungsgrundlage;
  if (rule.einheit === 'MB') return (betrag * satz) / 100;
  if (rule.einheit === '%o') return (betrag * satz) / 1000;
  if (rule.einheit === '%') {
    const nettoFaktor = 100 / 119;
    if (basis === 'Monatsbeitrag') {
      if (rule.sparte === 'UBR') return betrag * 12 * (satz / 100);
      return betrag * 12 * nettoFaktor * (satz / 100);
    }
    if (basis === 'Jahresbeitrag') return betrag * nettoFaktor * (satz / 100);
    return (betrag * rule.faktor);
  }
  return 0;
}

/**
 * Wertungssumme nach Excel (Spalte K):
 * - MB: Betrag * Faktor (1) = Betrag
 * - %o: Betrag * Faktor
 * - % Monatsbeitrag: Betrag * (100/119) * Faktor * 12
 * - % Jahresbeitrag: Betrag * (100/119) * Faktor
 */
function berechneWS(rule: ProvisionRule, betrag: number): number {
  if (!betrag || isNaN(betrag)) return 0;
  const f = rule.faktor;
  const basis = rule.bemessungsgrundlage;
  if (rule.einheit === 'MB') return Math.round(betrag * f);
  if (rule.einheit === '%o') return Math.round(betrag * f);
  if (rule.einheit === '%') {
    const nettoFaktor = 100 / 119;
    if (basis === 'Monatsbeitrag') return Math.round(betrag * nettoFaktor * f * 12);
    if (basis === 'Jahresbeitrag') return Math.round(betrag * nettoFaktor * f);
  }
  return Math.round(betrag);
}

type Props = {
  currentUser?: { role?: string; level?: string };
  initialProvisionRules?: ProvisionRulesByStufe | null;
  onSaveProvisionRules?: (data: ProvisionRulesByStufe) => Promise<void>;
};

export default function ProvisionsView({ currentUser, initialProvisionRules, onSaveProvisionRules }: Props) {
  const { theme } = useTheme();
  const isDark = theme === 'dark' || (theme === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const isAdmin = currentUser?.role === 'admin';

  const [rulesByStufe, setRulesByStufe] = useState<ProvisionRulesByStufe>(() =>
    initialProvisionRules && typeof initialProvisionRules === 'object' && Array.isArray(initialProvisionRules.AGT)
      ? initialProvisionRules
      : (provisionRulesData as ProvisionRulesByStufe)
  );
  const [rulesLoaded, setRulesLoaded] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'ok' | 'error'>('idle');

  useEffect(() => {
    if (initialProvisionRules && typeof initialProvisionRules === 'object' && Array.isArray(initialProvisionRules.AGT)) {
      setRulesByStufe(initialProvisionRules);
    }
  }, [initialProvisionRules]);

  useEffect(() => {
    if (rulesLoaded) return;
    fetch('/api/provision-rules')
      .then(res => res.ok ? res.json() : Promise.reject())
      .then((data: ProvisionRulesByStufe) => {
        if (data && typeof data === 'object' && Array.isArray(data.AGT) && !initialProvisionRules) setRulesByStufe(data);
      })
      .catch(() => {})
      .finally(() => setRulesLoaded(true));
  }, [initialProvisionRules, rulesLoaded]);

  const stufeKeys = Object.keys(rulesByStufe).filter(k => Array.isArray(rulesByStufe[k]));
  const [activeTab, setActiveTab] = useState<'rechner' | 'provisionssaetze'>('rechner');
  /** Im Rechner: immer die Agenturstufe des eingeloggten Users; keine Auswahl. */
  const effectiveStufe = useMemo(() => {
    const key = userLevelToStufeKey(currentUser?.level);
    return stufeKeys.includes(key) ? key : (stufeKeys[0] || 'AGT');
  }, [currentUser?.level, stufeKeys]);
  /** Nur für Admin im Tab "Provisionssätze": Stufe zum Bearbeiten wählen. */
  const [adminEditStufe, setAdminEditStufe] = useState<string>(stufeKeys[0] || 'AGT');
  const selectedStufe = activeTab === 'provisionssaetze' && isAdmin ? adminEditStufe : effectiveStufe;
  useEffect(() => {
    if (activeTab === 'provisionssaetze' && isAdmin && !stufeKeys.includes(adminEditStufe)) {
      setAdminEditStufe(stufeKeys[0] || 'AGT');
    }
  }, [activeTab, isAdmin, adminEditStufe, stufeKeys]);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [betraege, setBetraege] = useState<Record<string, string>>({});

  const updateRule = useCallback((stufe: string, index: number, field: keyof ProvisionRule, value: string | number) => {
    setRulesByStufe(prev => {
      const arr = [...(prev[stufe] || [])];
      if (!arr[index]) return prev;
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, [stufe]: arr };
    });
  }, []);

  const handleSaveProvisionssaetze = useCallback(async () => {
    setSaveStatus('saving');
    try {
      if (onSaveProvisionRules) {
        await onSaveProvisionRules(rulesByStufe);
        setSaveStatus('ok');
        setTimeout(() => setSaveStatus('idle'), 2000);
      } else {
        const res = await fetch('/api/provision-rules', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(rulesByStufe),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.success) {
          setSaveStatus('ok');
          setTimeout(() => setSaveStatus('idle'), 2000);
        } else setSaveStatus('error');
      }
    } catch {
      setSaveStatus('error');
    }
  }, [rulesByStufe, onSaveProvisionRules]);

  const currentRules = useMemo(() => {
    const arr = rulesByStufe[selectedStufe];
    return Array.isArray(arr) ? arr.filter(r => r.bemessungsgrundlage !== 'Summe') : [];
  }, [rulesByStufe, selectedStufe]);

  const categories = useMemo(() => groupRulesBySparte(rulesByStufe[selectedStufe] || []), [rulesByStufe, selectedStufe]);

  const getBetragKey = useCallback((catName: string, ruleIndex: number) => `${selectedStufe}-${catName}-${ruleIndex}`, [selectedStufe]);
  const parseBetrag = (v: string): number => {
    if (!v || typeof v !== 'string') return 0;
    let str = v.replace(/[^\d,.-]/g, '').trim();
    if (str.includes('.') && str.includes(',') && str.lastIndexOf(',') > str.lastIndexOf('.')) {
      str = str.replace(/\./g, '').replace(',', '.');
    } else if (str.includes(',')) {
      str = str.replace(',', '.');
    }
    const num = parseFloat(str);
    return isNaN(num) ? 0 : num;
  };
  const getBetrag = useCallback((catName: string, ruleIndex: number) => {
    const v = betraege[getBetragKey(catName, ruleIndex)];
    return typeof v === 'string' ? parseBetrag(v) : (typeof v === 'number' ? v : 0);
  }, [betraege, getBetragKey]);
  const setBetrag = useCallback((catName: string, ruleIndex: number, value: string) => {
    setBetraege(prev => ({ ...prev, [getBetragKey(catName, ruleIndex)]: value }));
  }, [getBetragKey]);

  const handleZuruecksetzen = useCallback(() => {
    setBetraege({});
  }, []);

  const toggleCollapsed = useCallback((name: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  return (
    <PageLayout>
      <PageHeader
        title="Provisionsrechner"
        subtitle="Signal Iduna – Provisions- und Wertungssummenberechnung"
        actions={
          <>
            <div className={`px-4 py-2 rounded-xl border ${isDark ? 'bg-white/10 border-white/20' : 'bg-white border-slate-200'}`}>
              <span className={`block text-xs font-semibold mb-1 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>Ihre Agenturstufe</span>
              <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{STUFE_LABELS[effectiveStufe] || effectiveStufe}</span>
            </div>
            <button
              type="button"
              onClick={handleZuruecksetzen}
              className={`px-4 py-2 rounded-xl border font-medium flex items-center gap-2 ${isDark ? 'bg-white/10 border-white/20 text-white hover:bg-white/20' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}
            >
              <RotateCcw size={18} />
              Zurücksetzen
            </button>
          </>
        }
      />

      <PageCard className="p-2">
      <div className="flex gap-2">
        {[
          { id: 'rechner' as const, label: 'Rechner', icon: Calculator },
          { id: 'provisionssaetze' as const, label: 'Provisionssätze', icon: FileSpreadsheet },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-4 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
              activeTab === tab.id
                ? isDark
                  ? 'bg-gradient-to-r from-sky-500/40 to-blue-500/40 text-white border border-sky-400/50'
                  : 'bg-gradient-to-r from-sky-100 to-blue-100 text-sky-900 border border-sky-300'
                : isDark
                  ? 'text-white/60 hover:text-white/80'
                  : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <tab.icon size={18} />
            {tab.label}
            {activeTab === tab.id && <ChevronRight size={18} />}
          </button>
        ))}
      </div>      {/* Tab: Rechner */}
      {activeTab === 'rechner' && (
        <div className="space-y-4">
          {categories.map(({ name, rules }) => {
            const isCollapsed = collapsed.has(name);
            return (
              <div
                key={name}
                className={`rounded-2xl border overflow-hidden ${isDark ? 'glass-card border-white/20' : 'bg-white border-slate-200 shadow-sm'}`}
              >
                <button
                  type="button"
                  onClick={() => toggleCollapsed(name)}
                  className={`w-full flex items-center justify-between gap-3 p-4 text-left ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
                >
                  <div className="flex items-center gap-3">
                    <span className={isDark ? 'text-sky-400' : 'text-sky-600'}>{getSparteIcon(name)}</span>
                    <span className={`font-bold text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>{name}</span>
                    <span className={`text-sm ${isDark ? 'text-white/60' : 'text-slate-500'}`}>{rules.length} Tarif{rules.length !== 1 ? 'e' : ''}</span>
                  </div>
                  <span className={isDark ? 'text-white/70' : 'text-slate-500'}>
                    {isCollapsed ? <ChevronRight size={20} /> : <ChevronDown size={20} />}
                  </span>
                </button>
                {!isCollapsed && (
                  <div className={`border-t ${isDark ? 'border-white/10' : 'border-slate-100'}`}>
                    {rules.map((rule, idx) => {
                      const betrag = getBetrag(name, idx);
                      const prov = berechneProvision(rule, betrag);
                      const ws = berechneWS(rule, betrag);
                      const key = getBetragKey(name, idx);
                      const inputVal = betraege[key] ?? '';
                      return (
                        <div
                          key={idx}
                          className={`grid grid-cols-[1fr_auto_120px_120px_100px] sm:grid-cols-[1fr_100px_120px_140px_90px] gap-3 items-center px-4 py-3 border-b last:border-b-0 ${isDark ? 'border-white/10' : 'border-slate-100'}`}
                        >
                          <div className={`text-sm ${isDark ? 'text-white/90' : 'text-slate-800'}`}>
                            {[rule.neuVerl?.trim(), rule.bemessungsgrundlage].filter(Boolean).join(' ')}
                          </div>
                          <div className={`text-sm font-medium ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                            Satz: {rule.provisionssatz} {rule.einheit}
                          </div>
                          <div>
                            <input
                              type="text"
                              inputMode="decimal"
                              placeholder="0.00"
                              value={inputVal}
                              onChange={e => setBetrag(name, idx, e.target.value)}
                              className={`w-full px-3 py-2 rounded-lg border text-sm text-right ${isDark ? 'bg-white/10 border-white/20 text-white placeholder-white/50' : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'}`}
                            />
                          </div>
                          <div className={`px-3 py-2 rounded-lg text-sm font-semibold text-center ${isDark ? 'bg-green-500/20 text-green-300' : 'bg-green-100 text-green-800'}`}>
                            {prov.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                          </div>
                          <div className={`px-3 py-2 rounded-lg text-sm font-semibold text-center ${isDark ? 'bg-sky-500/20 text-sky-300' : 'bg-sky-100 text-sky-800'}`}>
                            {ws} WS
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}      {activeTab === 'provisionssaetze' && (
        <PageCard>
          <div className="flex flex-wrap items-center justify-between gap-4 p-6 pb-2">
            <div className="flex flex-wrap items-center gap-4">
              <h3 className={`text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                <FileSpreadsheet size={22} className={isDark ? 'text-amber-400' : 'text-amber-600'} />
                Provisionssätze{isAdmin ? null : ` – ${STUFE_LABELS[selectedStufe] || selectedStufe}`}
              </h3>
              {isAdmin && (
                <select
                  value={adminEditStufe}
                  onChange={e => setAdminEditStufe(e.target.value)}
                  className={`px-3 py-2 rounded-xl border text-sm font-medium ${isDark ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                >
                  {stufeKeys.map(key => (
                    <option key={key} value={key}>{STUFE_LABELS[key] || key}</option>
                  ))}
                </select>
              )}
            </div>
            <div>
              {isAdmin && (
                <p className={`text-sm mt-1 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                  Satz und Faktor bearbeiten, dann „Speichern“ (alle Agenturstufen).
                </p>
              )}
            </div>
            {isAdmin && (
              <button
                type="button"
                onClick={handleSaveProvisionssaetze}
                disabled={saveStatus === 'saving'}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all ${
                  saveStatus === 'ok'
                    ? 'bg-green-500/20 text-green-600 border border-green-400/50'
                    : saveStatus === 'error'
                      ? 'bg-red-500/20 text-red-600 border border-red-400/50'
                      : isDark
                        ? 'bg-amber-500/20 text-amber-200 border border-amber-400/40 hover:bg-amber-500/30'
                        : 'bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200'
                }`}
              >
                <Save size={18} />
                {saveStatus === 'saving' ? 'Speichern…' : saveStatus === 'ok' ? 'Gespeichert' : 'Speichern'}
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={isDark ? 'border-white/20 bg-white/5' : 'border-slate-200 bg-slate-50'}>
                  <th className={`text-left py-3 px-4 text-sm font-semibold ${isDark ? 'text-white/90' : 'text-slate-700'}`}>Sparte</th>
                  <th className={`text-left py-3 px-4 text-sm font-semibold ${isDark ? 'text-white/90' : 'text-slate-700'}`}>Bemessungsgrundlage</th>
                  <th className={`text-left py-3 px-4 text-sm font-semibold ${isDark ? 'text-white/90' : 'text-slate-700'}`}>Neu/Verl.</th>
                  <th className={`text-right py-3 px-4 text-sm font-semibold ${isDark ? 'text-white/90' : 'text-slate-700'}`}>Satz</th>
                  <th className={`text-left py-3 px-4 text-sm font-semibold ${isDark ? 'text-white/90' : 'text-slate-700'}`}>Einheit</th>
                  {isAdmin && (
                    <th className={`text-right py-3 px-4 text-sm font-semibold ${isDark ? 'text-white/90' : 'text-slate-700'}`}>Faktor</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {currentRules.map((rule, i) => (
                  <tr key={i} className={isDark ? 'border-white/10 hover:bg-white/5' : 'border-slate-100 hover:bg-slate-50'}>
                    <td className={`py-2.5 px-4 text-sm ${isDark ? 'text-white/90' : 'text-slate-800'}`}>{rule.sparte || '–'}</td>
                    <td className={`py-2.5 px-4 text-sm ${isDark ? 'text-white/80' : 'text-slate-700'}`}>{rule.bemessungsgrundlage}</td>
                    <td className={`py-2.5 px-4 text-sm ${isDark ? 'text-white/80' : 'text-slate-700'}`}>{rule.neuVerl?.trim() || '–'}</td>
                    <td className={`py-2.5 px-4 text-sm text-right ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                      {isAdmin ? (
                        <input
                          type="number"
                          step="0.01"
                          value={rule.provisionssatz}
                          onChange={e => updateRule(selectedStufe, i, 'provisionssatz', parseFloat(e.target.value) || 0)}
                          className={`w-20 px-2 py-1 rounded border text-right text-sm font-medium ${isDark ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                        />
                      ) : (
                        <span className="font-medium">{rule.provisionssatz}</span>
                      )}
                      {' '}{rule.einheit}
                    </td>
                    <td className={`py-2.5 px-4 text-sm ${isDark ? 'text-white/70' : 'text-slate-600'}`}>{rule.einheit}</td>
                    {isAdmin && (
                      <td className="py-2.5 px-4 text-sm text-right">
                        <input
                          type="number"
                          step="0.0001"
                          value={rule.faktor}
                          onChange={e => updateRule(selectedStufe, i, 'faktor', parseFloat(e.target.value) || 0)}
                          className={`w-24 px-2 py-1 rounded border text-right text-sm ${isDark ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </PageCard>
      )}
      </PageCard>
    </PageLayout>
  );
}